import { useState, useEffect } from 'react';
import { collection, query, where, getDocs, limit } from 'firebase/firestore';
import { db } from '../config/firebase';
import { getFunctions, httpsCallable } from 'firebase/functions';

function Blocks() {
  const [blockedUsers, setBlockedUsers] = useState([]);
  const [activeTab, setActiveTab] = useState('incumplimiento'); // "incumplimiento" | "denuncia" | "falta_de_pago"
  const [loading, setLoading] = useState(true);
  const [processingId, setProcessingId] = useState(null);
  
  // Search state
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [isSearching, setIsSearching] = useState(false);
  const [selectedUser, setSelectedUser] = useState(null);
  
  // Block form state
  const [blockReason, setBlockReason] = useState('incumplimiento');
  const [blockDescription, setBlockDescription] = useState('');

  const fetchBlockedUsers = async () => {
    setLoading(true);
    try {
      // We need to fetch from both 'users' and 'drivers'
      const driversQuery = query(collection(db, 'drivers'), where('blocked', '==', true), where('blockReason', '==', activeTab));
      const usersQuery = query(collection(db, 'users'), where('blocked', '==', true), where('blockReason', '==', activeTab));
      
      const [driversSnap, usersSnap] = await Promise.all([getDocs(driversQuery), getDocs(usersQuery)]);
      
      const combined = [
        ...driversSnap.docs.map(doc => ({ id: doc.id, collection: 'driver', uniqueId: 'driver_' + doc.id, ...doc.data() })),
        ...usersSnap.docs.map(doc => ({ id: doc.id, collection: 'passenger', uniqueId: 'passenger_' + doc.id, ...doc.data() }))
      ];
      
      // Sort by blockedAt descending
      combined.sort((a, b) => {
        if (!a.blockedAt || !b.blockedAt) return 0;
        return b.blockedAt.toMillis() - a.blockedAt.toMillis();
      });
      
      setBlockedUsers(combined);
    } catch (error) {
      console.error("Error fetching blocked users:", error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchBlockedUsers();
  }, [activeTab]);

  useEffect(() => {
    const timer = setTimeout(() => {
      const qText = searchQuery.trim();
      if (qText.length < 3) {
        setSearchResults([]);
        return;
      }
      performSearch(qText);
    }, 500);

    return () => clearTimeout(timer);
  }, [searchQuery]);

  const performSearch = async (qText) => {
    setIsSearching(true);
    try {
      let results = [];
      const isPhone = qText.startsWith('+') || !isNaN(qText[0]);
      
      let queries = [];
      if (isPhone) {
        queries = [
          getDocs(query(collection(db, 'users'), where('phone', '>=', qText), where('phone', '<=', qText + '\uf8ff'), limit(5))),
          getDocs(query(collection(db, 'drivers'), where('phone', '>=', qText), where('phone', '<=', qText + '\uf8ff'), limit(5)))
        ];
      } else if (qText.includes('@')) {
        queries = [
          getDocs(query(collection(db, 'users'), where('email', '>=', qText), where('email', '<=', qText + '\uf8ff'), limit(5))),
          getDocs(query(collection(db, 'drivers'), where('email', '>=', qText), where('email', '<=', qText + '\uf8ff'), limit(5)))
        ];
      } else {
        // Search by name, trying capitalized and uncapitalized just in case
        const qUpper = qText.charAt(0).toUpperCase() + qText.slice(1);
        const qLower = qText.toLowerCase();
        
        queries = [
          getDocs(query(collection(db, 'users'), where('name', '>=', qUpper), where('name', '<=', qUpper + '\uf8ff'), limit(5))),
          getDocs(query(collection(db, 'drivers'), where('name', '>=', qUpper), where('name', '<=', qUpper + '\uf8ff'), limit(5))),
          getDocs(query(collection(db, 'users'), where('name', '>=', qLower), where('name', '<=', qLower + '\uf8ff'), limit(5))),
          getDocs(query(collection(db, 'drivers'), where('name', '>=', qLower), where('name', '<=', qLower + '\uf8ff'), limit(5)))
        ];
      }
      
      const snaps = await Promise.all(queries);
      const seenIds = new Set();
      
      snaps.forEach((snap, idx) => {
        // En los índices pares buscamos 'users', en los impares 'drivers' (según el array queries)
        // Para el caso del nombre, idx 0 y 2 son users, idx 1 y 3 son drivers
        const type = (idx % 2 === 0) ? 'passenger' : 'driver';
        snap.docs.forEach(doc => {
          const uniqueId = type + '_' + doc.id;
          if (!seenIds.has(uniqueId)) {
            seenIds.add(uniqueId);
            results.push({ id: doc.id, collection: type, uniqueId, ...doc.data() });
          }
        });
      });
      
      setSearchResults(results);
    } catch (error) {
      console.error("Error searching:", error);
    } finally {
      setIsSearching(false);
    }
  };

  const handleSearch = (e) => {
    e.preventDefault();
    const qText = searchQuery.trim();
    if (qText.length >= 3) {
      performSearch(qText);
    }
  };

  const handleBlockUser = async (e) => {
    e.preventDefault();
    if (!selectedUser) return;
    
    if (!confirm(`¿Estás seguro de que deseas BLOQUEAR a ${selectedUser.name}?`)) return;
    
    setProcessingId(selectedUser.id);
    try {
      const functions = getFunctions();
      const blockUserFn = httpsCallable(functions, 'blockUser');
      
      await blockUserFn({
        targetId: selectedUser.id,
        targetType: selectedUser.collection,
        reason: blockReason,
        description: blockDescription
      });
      
      alert("Usuario bloqueado exitosamente");
      setSelectedUser(null);
      setSearchQuery('');
      setSearchResults([]);
      setBlockDescription('');
      
      if (activeTab === blockReason) {
        fetchBlockedUsers();
      } else {
        setActiveTab(blockReason);
      }
    } catch (error) {
      console.error("Error blocking user:", error);
      alert(`Error al bloquear: ${error.message}`);
    } finally {
      setProcessingId(null);
    }
  };

  const handleUnblockUser = async (user) => {
    if (!confirm(`¿Estás seguro de que deseas DESBLOQUEAR a ${user.name}?`)) return;
    
    setProcessingId(user.id);
    try {
      const functions = getFunctions();
      const unblockUserFn = httpsCallable(functions, 'unblockUser');
      
      await unblockUserFn({
        targetId: user.id,
        targetType: user.collection
      });
      
      setBlockedUsers(blockedUsers.filter(u => u.id !== user.id));
    } catch (error) {
      console.error("Error unblocking user:", error);
      alert(`Error al desbloquear: ${error.message}`);
    } finally {
      setProcessingId(null);
    }
  };

  const tabClass = (tab) => {
    return `tab-btn ${activeTab === tab ? 'active' : ''}`;
  };

  const formatDate = (timestamp) => {
    if (!timestamp) return 'Desconocida';
    return timestamp.toDate().toLocaleString('es-VE');
  };

  return (
    <div className="animate-fade-in">
      <div className="page-header">
        <h2 className="page-title">Bloqueos del Sistema</h2>
        <p className="page-subtitle">Gestiona usuarios y conductores bloqueados por diversas razones</p>
      </div>

      {/* SEARCH AND BLOCK SECTION */}
      <div className="glass-panel" style={{ marginBottom: '24px' }}>
        <h3 style={{ margin: '0 0 16px 0', fontSize: '18px' }}>Bloquear Usuario / Conductor</h3>
        <form onSubmit={handleSearch} style={{ display: 'flex', gap: '12px', marginBottom: '16px' }}>
          <input
            type="text"
            className="input-field"
            placeholder="Buscar por Nombre, Email o Teléfono... (min. 3 caracteres)"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            style={{ flex: 1 }}
          />
          <button type="submit" className="btn btn-primary" disabled={isSearching || !searchQuery}>
            {isSearching ? 'Buscando...' : 'Buscar'}
          </button>
        </form>

        {searchResults.length > 0 && !selectedUser && (
          <div style={{ marginTop: '16px' }}>
            <h4>Resultados ({searchResults.length}):</h4>
            <div style={{ display: 'grid', gap: '12px', marginTop: '12px' }}>
              {searchResults.map(user => (
                <div key={user.uniqueId} className="user-card" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '12px', background: 'rgba(255,255,255,0.05)', borderRadius: '8px' }}>
                  <div>
                    <strong>{user.name}</strong> 
                    <span className={`status-badge status-${user.collection === 'driver' ? 'online' : 'busy'}`} style={{ marginLeft: '8px' }}>
                      {user.collection === 'driver' ? 'Conductor' : 'Pasajero'}
                    </span>
                    <div style={{ fontSize: '14px', color: '#94a3b8', marginTop: '4px' }}>
                      {user.email} • {user.phone}
                    </div>
                  </div>
                  {user.blocked ? (
                    <span className="status-badge status-offline">Ya bloqueado</span>
                  ) : (
                    <button className="btn btn-danger" onClick={() => setSelectedUser(user)}>
                      Seleccionar para Bloquear
                    </button>
                  )}
                </div>
              ))}
            </div>
          </div>
        )}

        {selectedUser && (
          <div style={{ marginTop: '20px', padding: '16px', background: 'rgba(239, 68, 68, 0.1)', borderRadius: '8px', border: '1px solid rgba(239, 68, 68, 0.3)' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
              <h4>Bloqueando a: {selectedUser.name} ({selectedUser.collection === 'driver' ? 'Conductor' : 'Pasajero'})</h4>
              <button className="btn" style={{ background: 'transparent', border: 'none', color: '#fff' }} onClick={() => setSelectedUser(null)}>✕ Cancelar</button>
            </div>
            
            <form onSubmit={handleBlockUser} style={{ marginTop: '16px' }}>
              <div style={{ display: 'grid', gap: '16px' }}>
                <div>
                  <label style={{ display: 'block', marginBottom: '8px', color: '#cbd5e1' }}>Motivo del Bloqueo</label>
                  <select 
                    className="input-field" 
                    value={blockReason} 
                    onChange={(e) => setBlockReason(e.target.value)}
                    style={{ width: '100%' }}
                  >
                    <option value="incumplimiento">Incumplimiento (Términos y Condiciones)</option>
                    <option value="denuncia">Denuncia (Reporte de usuarios)</option>
                    {selectedUser.collection === 'driver' && (
                      <option value="falta_de_pago">Falta de Pago (Solo Conductores)</option>
                    )}
                  </select>
                </div>
                
                <div>
                  <label style={{ display: 'block', marginBottom: '8px', color: '#cbd5e1' }}>Descripción / Notas (Opcional)</label>
                  <textarea 
                    className="input-field" 
                    placeholder="Detalles sobre por qué se está bloqueando a este usuario..."
                    value={blockDescription}
                    onChange={(e) => setBlockDescription(e.target.value)}
                    style={{ width: '100%', minHeight: '80px', resize: 'vertical' }}
                  ></textarea>
                </div>
                
                <button 
                  type="submit" 
                  className="btn btn-danger" 
                  style={{ width: '100%' }}
                  disabled={processingId === selectedUser.id}
                >
                  {processingId === selectedUser.id ? 'Procesando...' : 'Confirmar Bloqueo'}
                </button>
              </div>
            </form>
          </div>
        )}
      </div>

      {/* BLOCKED USERS LIST SECTION */}
      <div className="tabs" style={{ marginBottom: '20px' }}>
        <button onClick={() => setActiveTab('incumplimiento')} className={tabClass('incumplimiento')} style={activeTab === 'incumplimiento' ? {borderBottom: '2px solid #ef4444'} : {}}>
          Incumplimiento
        </button>
        <button onClick={() => setActiveTab('denuncia')} className={tabClass('denuncia')} style={activeTab === 'denuncia' ? {borderBottom: '2px solid #f97316'} : {}}>
          Denuncias
        </button>
        <button onClick={() => setActiveTab('falta_de_pago')} className={tabClass('falta_de_pago')} style={activeTab === 'falta_de_pago' ? {borderBottom: '2px solid #eab308'} : {}}>
          Falta de Pago
        </button>
      </div>

      <div className="glass-panel">
        {loading ? (
          <div className="spinner-container">
            <div className="spinner"></div>
            <p>Cargando lista de bloqueados...</p>
          </div>
        ) : blockedUsers.length === 0 ? (
          <div style={{ textAlign: 'center', padding: '40px', color: '#94a3b8' }}>
            <span style={{ fontSize: '48px', display: 'block', marginBottom: '16px' }}>✅</span>
            <p>No hay usuarios bloqueados en esta categoría.</p>
          </div>
        ) : (
          <div className="table-responsive">
            <table className="data-table">
              <thead>
                <tr>
                  <th>Usuario</th>
                  <th>Tipo</th>
                  <th>Motivo</th>
                  <th>Descripción</th>
                  <th>Fecha</th>
                  <th>Por</th>
                  <th>Acciones</th>
                </tr>
              </thead>
              <tbody>
                {blockedUsers.map(user => (
                  <tr key={user.uniqueId}>
                    <td>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                        <img 
                          src={user.photoUrl || 'https://via.placeholder.com/40'} 
                          alt="Foto" 
                          style={{ width: '40px', height: '40px', borderRadius: '50%', objectFit: 'cover' }}
                        />
                        <div>
                          <div style={{ fontWeight: '500' }}>{user.name}</div>
                          <div style={{ fontSize: '12px', color: '#94a3b8' }}>{user.phone}</div>
                        </div>
                      </div>
                    </td>
                    <td>
                      <span className={`status-badge status-${user.collection === 'driver' ? 'online' : 'busy'}`}>
                        {user.collection === 'driver' ? 'Conductor' : 'Pasajero'}
                      </span>
                    </td>
                    <td>
                      <span className="status-badge status-offline">
                        {user.blockReason === 'incumplimiento' ? 'Incumplimiento' : 
                         user.blockReason === 'denuncia' ? 'Denuncia' : 'Falta de Pago'}
                      </span>
                    </td>
                    <td style={{ maxWidth: '200px', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }} title={user.blockDescription}>
                      {user.blockDescription || '-'}
                    </td>
                    <td style={{ fontSize: '14px', color: '#94a3b8' }}>
                      {formatDate(user.blockedAt)}
                    </td>
                    <td style={{ fontSize: '14px' }}>
                      {user.blockedBy || 'admin'}
                    </td>
                    <td>
                      <button 
                        className="btn btn-primary" 
                        style={{ padding: '6px 12px', fontSize: '13px' }}
                        onClick={() => handleUnblockUser(user)}
                        disabled={processingId === user.id}
                      >
                        {processingId === user.id ? '...' : 'Desbloquear'}
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}

export default Blocks;
